import { useEffect, useRef } from "react";
import { YourAddressAutomatic } from "./YourAddressAutomatic";
import classes from "./YourAddress.module.scss";
import { YourAddressProps } from "./YourAddressProps";
import { YourAddressForm } from "./YourAddressForm";
import type { AddressFields } from "@/types/Address";
import { useDonationFormStore } from "@/stores";
import { useTranslation } from "@/hooks/useTranslation";
import { clsx } from "clsx";
import { ValidationGroupProvider } from "@/contexts/ValidationGroupProvider";

const emptyAddress: AddressFields = {
  "address-line-1": "",
  "address-line-2": "",
  city: "",
  country: "",
  region: "",
  "postal-code": "",
};

export function YourAddress(props: YourAddressProps) {
  const { className, config, ref } = props;

  const { isManualAddress, setIsManualAddress } = useDonationFormStore();

  const headerRef = useRef<HTMLDivElement>(null);
  const t = useTranslation("your_address");

  const setFieldValue = useDonationFormStore((state) => state.setFieldValue);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const link = header.querySelector(
      "[data-manual-link]",
    ) as HTMLAnchorElement | null;

    const handleClick = (event: MouseEvent) => {
      event.preventDefault();

      setFieldValue("address", emptyAddress);
      setIsManualAddress(true);
    };

    if (link) {
      link.addEventListener("click", handleClick);
    }

    return () => {
      if (link) {
        link.removeEventListener("click", handleClick);
      }
    };
  }, [setFieldValue, setIsManualAddress]);

  const handleSelect = (address: AddressFields) => {
    setFieldValue("address", address);
    setIsManualAddress(true);
  };

  return (
    <ValidationGroupProvider name="address" ref={ref}>
      <div className={className}>
        <div
          className={clsx(classes.header, classes.formatter)}
          ref={headerRef}
        >
          {t(config["your-address-body"].i18n, {
            manual_link: `<a href="#" data-manual-link="true">${t("enter-it-manually")}</a>`,
          })}
        </div>

        {isManualAddress && <YourAddressForm config={config} />}

        {!isManualAddress && (
          <YourAddressAutomatic config={config} onSelect={handleSelect} />
        )}

        <div className={clsx(classes.footer, classes.formatter)}>
          {t(config["your-address-footer-body"].i18n)}
        </div>
      </div>
    </ValidationGroupProvider>
  );
}
